// Selector de período estándar de la app (patrón C5: Hoy / Esta semana /
// Este mes / Rango) para las vistas de Facturación. El FE resuelve el período
// a {desde, hasta} y el API siempre recibe el rango explícito.

import { useMemo, useRef, useState } from 'react';
import { inicioSemanaOperativa } from 'schemas';
import { Input } from '../../components/ui/input';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { useTenantConfig } from '../../lib/api/useTenantConfig';

export type Periodo = 'hoy' | 'semana' | 'mes' | 'rango';

/** Fecha local YYYY-MM-DD. */
function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function periodoARango(
  periodo: Exclude<Periodo, 'rango'>,
  diaInicioSemana = 1,
): { desde: string; hasta: string } {
  const hoy = new Date();
  const hastaStr = ymd(hoy);
  if (periodo === 'hoy') return { desde: hastaStr, hasta: hastaStr };
  // Semana operativa del tenant (con default 1 = lunes de la semana actual).
  if (periodo === 'semana')
    return { desde: inicioSemanaOperativa(hastaStr, diaInicioSemana), hasta: hastaStr };
  return { desde: ymd(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), hasta: hastaStr };
}

/**
 * Estado del selector + rango efectivo. Si el rango personalizado es inválido,
 * congela el último rango válido (no dispara queries incoherentes — patrón C5).
 */
export function usePeriodoRango(inicial: Periodo = 'semana') {
  const { diaInicioSemana } = useTenantConfig();
  const [periodo, setPeriodo] = useState<Periodo>(inicial);
  const [rango, setRango] = useState<{ desde: string; hasta: string }>(() => {
    const hoy = new Date();
    const inicio = new Date(hoy);
    inicio.setDate(hoy.getDate() - 6);
    return { desde: ymd(inicio), hasta: ymd(hoy) };
  });
  const rangoValido = Boolean(rango.desde && rango.hasta && rango.desde <= rango.hasta);

  const efectivo = useMemo(
    () => (periodo === 'rango' ? rango : periodoARango(periodo, diaInicioSemana)),
    [periodo, rango, diaInicioSemana],
  );
  const commitRef = useRef(efectivo);
  if (periodo !== 'rango' || rangoValido) commitRef.current = efectivo;

  return {
    periodo,
    setPeriodo,
    rango,
    setRango,
    rangoValido,
    /** {desde, hasta} para el API — siempre coherente. */
    rangoEfectivo: commitRef.current,
  };
}

export function PeriodoSelector({
  periodo,
  onPeriodo,
  rango,
  onRango,
  rangoValido,
}: {
  periodo: Periodo;
  onPeriodo: (p: Periodo) => void;
  rango: { desde: string; hasta: string };
  onRango: (r: { desde: string; hasta: string }) => void;
  rangoValido: boolean;
}) {
  return (
    <div className="flex flex-col items-end gap-2">
      <SegmentedControl<Periodo>
        size="sm"
        value={periodo}
        onChange={onPeriodo}
        options={[
          { value: 'hoy', label: 'Hoy' },
          { value: 'semana', label: 'Esta semana' },
          { value: 'mes', label: 'Este mes' },
          { value: 'rango', label: 'Rango' },
        ]}
      />
      {periodo === 'rango' ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Desde
            </span>
            <Input
              type="date"
              value={rango.desde}
              max={rango.hasta || undefined}
              onChange={(e) => onRango({ ...rango, desde: e.target.value })}
              className="h-8 w-[150px]"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Hasta
            </span>
            <Input
              type="date"
              value={rango.hasta}
              min={rango.desde || undefined}
              onChange={(e) => onRango({ ...rango, hasta: e.target.value })}
              className="h-8 w-[150px]"
            />
          </div>
          {!rangoValido ? (
            <span className="text-xs text-crit">“Desde” debe ser anterior a “Hasta”.</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
